import React, { useState, useRef, useEffect, Fragment } from "react";
import {
  FaPaperPlane,
  FaFile,
  FaUsers,
  FaBell,
  FaHome,
  FaSearch,
} from "react-icons/fa";
import { auth, db, storage } from "../../Auth/firebase";
import {
  collection,
  query,
  onSnapshot,
  addDoc,
  serverTimestamp,
  orderBy,
  where,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import emailjs from "emailjs-com";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useProjects } from "../../../context/ProjectsContext";
import { Dialog, Transition } from "@headlessui/react";

const uploadFile = async (file) => {
  const storage = getStorage();
  const storageRef = ref(storage, `files/${file.name}_${Date.now()}`);
  await uploadBytes(storageRef, file);
  const downloadURL = await getDownloadURL(storageRef);
  return downloadURL;
};

const Chat = () => {
  const [inputs, setInputs] = useState({});
  const [messages, setMessages] = useState([]);
  const [usersData, setUsersData] = useState({});
  const [directMessages, setDirectMessages] = useState([]);
  const [activeDirectMessageUserId, setActiveDirectMessageUserId] =
    useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [notificationsDisabled, setNotificationsDisabled] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { currentUser } = auth;
  const { activeProject } = useProjects(); // Get the active project from context
  const endOfMessagesRef = useRef(null);
  const navigate = useNavigate();

  const getTimeString = (timestamp) => {
    if (!timestamp) return ""; // Return an empty string if timestamp is not available
    const date = new Date(timestamp.seconds * 1000); // Convert seconds to milliseconds
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    const formattedHours = hours % 12 === 0 ? 12 : hours % 12;
    return `${formattedHours}:${minutes} ${ampm}`;
  };

  useEffect(() => {
    const usersQuery = query(collection(db, "users"));

    const unsubscribeUsers = onSnapshot(usersQuery, (querySnapshot) => {
      const users = {};
      querySnapshot.forEach((userDoc) => {
        const userData = userDoc.data();
        users[userDoc.id] = userData;
      });
      setUsersData(users);
    });

    return () => unsubscribeUsers();
  }, []);

  useEffect(() => {
    if (endOfMessagesRef.current) {
      endOfMessagesRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  }, [messages]); // This will trigger whenever the 'messages' state updates.

  useEffect(() => {
    if (!activeDirectMessageUserId || !activeProject) {
      setMessages([]);
      return;
    }

    console.log("Active Project ID:", activeProject.id);

    const q = query(
      collection(db, "messages"),
      where("type", "==", "direct"),
      where("participants", "array-contains", currentUser.uid),
      where("projectId", "==", activeProject.id),
      orderBy("timestamp", "asc")
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const newMessages = querySnapshot.docs
        .map((doc) => {
          const data = doc.data();
          console.log("Fetched Message Data:", data);
          return {
            ...data,
            id: doc.id,
            username: usersData[data.user.uid]?.name || "Anonymous",
            isUser: data.user.uid === currentUser.uid,
          };
        })
        .filter((msg) => msg.participants.includes(activeDirectMessageUserId));

      setMessages(newMessages);
      console.log("New Messages:", newMessages);
    });

    return () => unsubscribe();
  }, [activeDirectMessageUserId, currentUser.uid, usersData, activeProject]);

  useEffect(() => {
    if (!activeProject) {
      setDirectMessages([]);
      return;
    }

    const projectAssignees = activeProject.assignees || [];
    const directMessagesData = projectAssignees
      .filter((userId) => userId !== currentUser.uid)
      .map((userId) => ({
        userId,
        username: usersData[userId]?.displayName || "Anonymous",
        photoURL:
          usersData[userId]?.profileImage ||
          "https://via.placeholder.com/40x40",
      }));

    setDirectMessages(directMessagesData);
  }, [activeProject, usersData, currentUser.uid]);

  const handleSend = async () => {
    const text = inputs[activeDirectMessageUserId]?.trim();
    if (!text) return; // Do nothing if the text is empty or only whitespace

    const messageData = {
      text: text,
      type: "direct",
      participants: [currentUser.uid, activeDirectMessageUserId],
      user: {
        uid: currentUser.uid,
        username: usersData[currentUser.uid]?.name || "Anonymous",
      },
      projectId: activeProject.id, // Include the active project ID
      timestamp: serverTimestamp(),
    };

    try {
      await addDoc(collection(db, "messages"), messageData);

      if (!notificationsDisabled) {
        const emailParams = {
          to_name: usersData[activeDirectMessageUserId]?.name || "User",
          to_email: getEmailFromUID(activeDirectMessageUserId),
          from_name: currentUser.displayName || "CollabPro",
          message: text,
          reply_to: "abdulrafeh380@gmail.com",
        };

        emailjs
          .send(
            "service_wz2z4do",
            "template_00jqane",
            emailParams,
            "oMXWMelsfkS3iY97x"
          )
          .then(
            (response) => {
              console.log(
                "Email sent successfully!",
                response.status,
                response.text
              );
            },
            (error) => {
              console.error("Failed to send email:", error);
            }
          );
      }

      setInputs((prevInputs) => ({
        ...prevInputs,
        [activeDirectMessageUserId]: "",
      }));

      if (endOfMessagesRef.current) {
        endOfMessagesRef.current.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const handleInputChange = (e, userId) => {
    const { value } = e.target;
    setInputs((prevInputs) => ({ ...prevInputs, [userId]: value }));
  };

  const handleKeyPress = (e, userId) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend(userId);
    }
  };

  const getEmailFromUID = (uid) => {
    const user = usersData[uid];
    return user ? user.email : "";
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const downloadURL = await uploadFile(file);

    const messageData = {
      text: `File: ${file.name}`,
      fileUrl: downloadURL,
      type: "direct",
      participants: [currentUser.uid, activeDirectMessageUserId],
      user: {
        uid: currentUser.uid,
        username: usersData[currentUser.uid]?.name || "Anonymous",
      },
      projectId: activeProject.id, // Include the active project ID
      timestamp: serverTimestamp(),
    };

    try {
      await addDoc(collection(db, "messages"), messageData);
      if (endOfMessagesRef.current) {
        endOfMessagesRef.current.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }
    } catch (error) {
      console.error("Error sending file message:", error);
    }
  };

  const filteredDirectMessages = directMessages.filter((dm) =>
    dm.username.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleToggleNotifications = () => {
    setIsModalOpen(true);
  };

  const confirmToggleNotifications = () => {
    setNotificationsDisabled(!notificationsDisabled);
    setIsModalOpen(false);
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <div className="w-1/4 h-[90vh] bg-white p-4 border-r flex flex-col overflow-hidden">
        {/* Direct message selection */}
        <div className="text-lg font-semibold text-gray-800 mb-2">
          Direct Messages
        </div>
        <div className="mb-4">
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-300"
          />
        </div>
        <div className="mb-4 overflow-y-auto flex-1">
          {filteredDirectMessages.map((directMessage) => (
            <div
              key={directMessage.userId}
              onClick={() => setActiveDirectMessageUserId(directMessage.userId)}
              className={`p-3 mt-2 rounded-md bg-gray-100 hover:bg-gray-300 cursor-pointer ${
                activeDirectMessageUserId === directMessage.userId
                  ? "bg-gray-300"
                  : ""
              }`}
            >
              <div className="flex items-center space-x-2">
                <img
                  src={directMessage.photoURL}
                  alt="Profile"
                  className="w-7 h-7 rounded-full"
                />
                <span className="font-semibold">{directMessage.username}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="flex-grow mb-4" />

        <div className="flex items-center justify-center space-x-4">
          <button
            className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center"
            onClick={() => navigate("/communication/channels")}
          >
            <FaUsers />
          </button>
          <button
            className={`w-10 h-10 rounded-full flex items-center justify-center ${
              notificationsDisabled ? "bg-red-200" : "bg-gray-200"
            }`}
            onClick={handleToggleNotifications}
          >
            <FaBell />
          </button>
          <button
            className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center"
            onClick={() => navigate("/")}
          >
            <FaHome />
          </button>
          <button className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
            <FaSearch />
          </button>
        </div>
      </div>

      <div className="w-3/4 flex h-[90vh] flex-col overflow-hidden">
        {activeDirectMessageUserId ? (
          <>
            {messages.length > 0 ? (
              <div className="flex-1 overflow-y-auto p-8">
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex items-start mb-4 ${
                      message.isUser ? "justify-end" : "justify-start"
                    }`}
                  >
                    <div
                      className={`${
                        message.isUser
                          ? "bg-blue-500 text-white"
                          : "bg-gray-300 text-gray-800"
                      } p-3 rounded-lg max-w-md`}
                    >
                      <div className="text-sm font-semibold mt-1">
                        {message.username}
                      </div>
                      {message.fileUrl ? (
                        <a
                          href={message.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-white underline"
                        >
                          {message.text}
                        </a>
                      ) : (
                        <p>{message.text}</p>
                      )}
                      <div
                        className={`text-xs mt-1 ${
                          message.isUser ? "text-white" : "text-gray-500"
                        }`}
                      >
                        {getTimeString(message.timestamp)}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={endOfMessagesRef} />
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center p-8">
                <p className="text-gray-500">
                  No messages yet. Start the conversation!
                </p>
              </div>
            )}

            <div className="h-20 p-4 border-t flex items-center bg-white">
              <textarea
                className="flex-1 p-2 mr-2 border rounded-md focus:outline-none focus:ring focus:border-blue-300"
                placeholder="Type your message..."
                value={inputs[activeDirectMessageUserId] || ""}
                onChange={(e) =>
                  setInputs({
                    ...inputs,
                    [activeDirectMessageUserId]: e.target.value,
                  })
                }
                onKeyPress={(e) => handleKeyPress(e, activeDirectMessageUserId)}
              />
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => handleSend(activeDirectMessageUserId)}
                  className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center hover:bg-blue-600"
                >
                  <FaPaperPlane />
                </button>
                <input
                  type="file"
                  id="fileInput"
                  className="hidden"
                  onChange={handleFileUpload}
                />
                <label
                  htmlFor="fileInput"
                  className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center cursor-pointer hover:bg-gray-400"
                >
                  <FaFile />
                </label>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-gray-500">Select a user to start chatting.</p>
          </div>
        )}
      </div>

      <Transition appear show={isModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-10"
          onClose={() => setIsModalOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    {notificationsDisabled
                      ? "Enable Notifications"
                      : "Disable Notifications"}
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-md text-gray-500">
                      {notificationsDisabled
                        ? "Are you sure you want to enable notifications for this project's DMs?"
                        : "Are you sure you want to disable notifications for this project's DMs?"}
                    </p>
                  </div>

                  <div className="mt-4 flex justify-end">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                      onClick={confirmToggleNotifications}
                    >
                      {notificationsDisabled ? "Enable" : "Disable"}
                    </button>
                    <button
                      type="button"
                      className="ml-3 inline-flex justify-center rounded-md border border-transparent bg-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                      onClick={() => setIsModalOpen(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default Chat;
